/**
 * `decouverteStore` — la mémoire du **mode découverte** ⟦A-9⟧, **dégonflé par ⟦A-129 · C11⟧**.
 *
 * `vues` porte TOUT ce que le joueur a déjà vu ou débloqué dans cette run — les leçons
 * bloquantes, les info-bulles non bloquantes, les explications de mini-jeu (`marquerVue`), ET
 * les éléments du HUD progressif (`game/hud.ts` la relit, mêmes ids que les leçons dont ils
 * partagent le déclencheur) : une seule mémoire, une seule clé `localStorage`, comme le demande
 * le chantier C11 (« même mémoire […] que la découverte »). À côté, deux « emplacements
 * d'écran » indépendants : `courante` (le voile bloquant) et `couranteBulle` (l'info-bulle
 * ancrée) — jamais les deux en même temps sur le même id, mais rien n'empêche l'un et l'autre
 * d'exister ensemble (un voile bloquant et une info-bulle ne parlent jamais du même sujet).
 *
 * **Pourquoi la mémoire vit ici et pas dans la run.** « Une seule fois par run, réactivable
 * dans les Réglages » : c'est de l'interface, pas de la simulation. La mettre dans `GameState`
 * salirait un état pur avec une préoccupation d'affichage, et le serveur autoritatif n'a rien à
 * faire de savoir quelles bulles d'aide un joueur a lues. Elle est donc rangée dans
 * `localStorage`, **clefée par `runId`** : une nouvelle chaîne rejoue tout le programme.
 */

import { create } from "zustand";

import { lecon, prochaineInfoBulle, prochaineLecon, type Lecon, type LeconId } from "../game/decouverte";
import type { GameState } from "@maxime/shared";

/** Versionnée comme les autres clés : un changement de forme jette proprement l'ancienne. */
const CLE = "maxime.decouverte.v1";

interface Memoire {
  runId: string;
  vues: LeconId[];
}

function lire(): Memoire | null {
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return null;
    const m = JSON.parse(brut) as Partial<Memoire>;
    if (typeof m.runId !== "string" || !Array.isArray(m.vues)) return null;
    return { runId: m.runId, vues: m.vues.filter((v): v is string => typeof v === "string") };
  } catch {
    return null;
  }
}

function ecrire(m: Memoire): void {
  try {
    localStorage.setItem(CLE, JSON.stringify(m));
  } catch {
    // Quota plein ou navigation privée : le programme se rejouera au prochain F5. Pas de quoi
    // casser la partie du joueur.
  }
}

interface DecouverteStore {
  /** La run à laquelle appartient `vues`. `null` tant qu'aucune run n'est chargée. */
  runId: string | null;
  vues: LeconId[];
  /** La leçon BLOQUANTE à l'écran. Tant qu'elle est là, **le jeu est figé**. */
  courante: Lecon | null;
  /**
   * ⟦A-129 · C11⟧ L'info-bulle NON bloquante à l'écran, ou `null`. Le jeu continue derrière —
   * ce slot n'a donc pas de `tourDeLaDerniere` : rien ne le limite à une fois par tour.
   */
  couranteBulle: Lecon | null;
  /** Le tour de la dernière leçon BLOQUANTE fermée : sert à n'en montrer qu'une par tour. */
  tourDeLaDerniere: number | null;

  /** À appeler quand la run change : recharge (ou repart de zéro) la mémoire. */
  synchroniser(runId: string): void;
  /**
   * Demande l'affichage d'une leçon bloquante. Sans effet si elle a déjà été vue, ou si une
   * autre est déjà à l'écran. Rend `true` si la leçon est (ou vient d'être) posée à l'écran.
   */
  demander(id: LeconId): boolean;
  /** Ferme la leçon bloquante courante et la marque vue. `tour` fait courir le compteur. */
  fermer(tour: number | null): void;
  /**
   * ⟦A-129 · C11⟧ Demande l'affichage d'une info-bulle non bloquante. Même contrat que
   * `demander`, sans le tour : une seule info-bulle à la fois, jamais plus d'une par tour.
   */
  demanderBulle(id: LeconId): boolean;
  /** Ferme l'info-bulle courante et la marque vue — pas de tour à mémoriser. */
  fermerBulle(): void;
  /**
   * ⟦A-129 · C11⟧ Marque une leçon vue SANS l'afficher — les explications de mini-jeu, qui
   * vivent désormais dans l'écran d'intro de l'Atelier lui-même plutôt que dans un des deux
   * emplacements ci-dessus. Idempotent.
   */
  marquerVue(id: LeconId): void;
  /** Le joueur a-t-il déjà vu cette leçon dans cette run ? */
  dejaVue(id: LeconId): boolean;
  /** ⟦A-9⟧ « réactivable » : tout le programme se rejoue depuis le début. */
  reinitialiser(): void;
}

export const useDecouverteStore = create<DecouverteStore>((set, get) => ({
  runId: null,
  vues: [],
  courante: null,
  couranteBulle: null,
  tourDeLaDerniere: null,

  synchroniser(runId) {
    if (get().runId === runId) return;
    const memoire = lire();
    set({
      runId,
      vues: memoire?.runId === runId ? memoire.vues : [],
      courante: null,
      couranteBulle: null,
      tourDeLaDerniere: null,
    });
  },

  demander(id) {
    const { courante, vues } = get();
    if (courante) return courante.id === id;
    if (vues.includes(id)) return false;
    const l = lecon(id);
    if (!l) return false;
    set({ courante: l });
    return true;
  },

  fermer(tour) {
    const { courante, vues, runId } = get();
    if (!courante) return;
    const suivantes = vues.includes(courante.id) ? vues : [...vues, courante.id];
    set({ vues: suivantes, courante: null, tourDeLaDerniere: tour });
    if (runId) ecrire({ runId, vues: suivantes });
  },

  demanderBulle(id) {
    const { couranteBulle, vues } = get();
    if (couranteBulle) return couranteBulle.id === id;
    if (vues.includes(id)) return false;
    const l = lecon(id);
    if (!l) return false;
    set({ couranteBulle: l });
    return true;
  },

  fermerBulle() {
    const { couranteBulle, vues, runId } = get();
    if (!couranteBulle) return;
    const suivantes = vues.includes(couranteBulle.id) ? vues : [...vues, couranteBulle.id];
    set({ vues: suivantes, couranteBulle: null });
    if (runId) ecrire({ runId, vues: suivantes });
  },

  marquerVue(id) {
    const { vues, runId } = get();
    if (vues.includes(id)) return;
    const suivantes = [...vues, id];
    set({ vues: suivantes });
    if (runId) ecrire({ runId, vues: suivantes });
  },

  dejaVue(id) {
    return get().vues.includes(id);
  },

  reinitialiser() {
    const { runId } = get();
    set({ vues: [], courante: null, couranteBulle: null, tourDeLaDerniere: null });
    if (runId) ecrire({ runId, vues: [] });
  },
}));

/**
 * La prochaine leçon BLOQUANTE due, ou `null`. Extrait du composant pour rester testable et
 * pour que la règle « une leçon par tour » n'ait qu'un seul endroit où vivre
 * (`game/decouverte.ts`).
 */
export function leconDue(state: GameState): Lecon | null {
  const { vues, tourDeLaDerniere } = useDecouverteStore.getState();
  return prochaineLecon(state, new Set(vues), tourDeLaDerniere);
}

/** ⟦A-129 · C11⟧ La prochaine info-bulle NON bloquante due, ou `null`. */
export function infoBulleDue(state: GameState): Lecon | null {
  const { vues } = useDecouverteStore.getState();
  return prochaineInfoBulle(state, new Set(vues));
}
